/**
 * Per-step emails panel (AR-328) — subject · to · trigger.
 *
 * Every step type gets one, including system steps, where it is the only place
 * an admin can see which mail the step sends.
 */
import React from 'react';
import { Mail, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ActivationEmail } from '@/lib/activationBuilder';
import { PanelSectionLabel } from './activationPrimitives';

interface StepEmailsPanelProps {
  emails: ActivationEmail[];
  /** System steps list their mail read-only. */
  readOnly?: boolean;
}

export const StepEmailsPanel: React.FC<StepEmailsPanelProps> = ({ emails, readOnly = false }) => (
  <section>
    <PanelSectionLabel>Email notifications</PanelSectionLabel>

    {emails.length ? (
      <ul className="space-y-2">
        {emails.map((email) => (
          <li
            key={`${email.subject}-${email.trigger}`}
            className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900"
          >
            <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold text-gray-900 dark:text-gray-100">
                {email.subject}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                To {email.to} · {email.trigger}
              </p>
            </div>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-xs text-gray-400 dark:text-gray-500">No emails triggered by this step.</p>
    )}

    {!readOnly && (
      <Button variant="ghost" size="sm" className="mt-2 h-7 px-2 text-xs text-orange-600 dark:text-orange-400">
        <Plus className="mr-1 h-3 w-3" /> Add notification
      </Button>
    )}
  </section>
);

export default StepEmailsPanel;
